import { PreTransaction, AuthorizationStatusEnum } from "@/app/services/authorizationService/getAuthorization";
import { BasicTable } from "@/view/components/BasicTable";
import { Input } from "@/view/components/ui/input";
import { RefreshCw, Search } from "lucide-react";
import { Button } from "@/view/components/ui/button";
import { AuthorizationSheet } from "./components/AuthorizationSheet";
import { useAuthorizationTableController } from "./useAuthorizationTableController";
import { ModalSimulatePan } from "../TransactionTable/components/TransactionDetails/components/SimulatePanModal";
import { BasicFilter } from "@/view/components/BasicFilter";
import { MobileAuthorizationList } from "./components/MobileAuthorizationList";
import Select from "react-select";
import { FilterSelectStyle } from "@/view/components/BasicFilter/FilterSelectStyle";
import { MultiValue } from "react-select";
import { OptionType } from "@/app/utils/optionSelectType";
import { PartnerType } from "@/app/utils/partners.enum";
import { useMemo, useState } from "react";
import { useProductsCatalog } from "@/app/hooks/useProductsCatalog";
import {
  authorizationProductTypes,
  authorizationStatusOptions,
} from "@/app/utils/authorization";

export const AuthorizationTable = () => {
  const {
    table,
    isLoading,
    isFetching,
    handleRowClick,
    selectedItem,
    sheetOpen,
    setSheetOpen,
    observerRef,
    isFetchingNextPage,
    shouldShowInfiniteScroll,
    handleSimulate,
    simulatePanOpen,
    setSimulatePanOpen,
    search,
    handleSearchInputChange,
    handleRefresh,
    handleApplyFilter,
    handleClearFilter,
    data,
    fetchNextPage,
  } = useAuthorizationTableController();

  const { catalog, getProductLabel } = useProductsCatalog();

  const authorizationTypeOptions = useMemo<OptionType[]>(
    () =>
      authorizationProductTypes.map((type) => ({
        value: type,
        label: getProductLabel(type),
      })),
    [getProductLabel],
  );

  const [pendingStatus, setPendingStatus] = useState<MultiValue<OptionType>>([]);
  const [pendingType, setPendingType] = useState<MultiValue<OptionType>>([]);

  const { DropdownIndicator, customStyles } = FilterSelectStyle({ isMulti: true });

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleApplyFilter(
      pendingStatus.map((o) => o.value as AuthorizationStatusEnum),
      pendingType.map((o) => o.value as PartnerType),
    );
  };

  const handleFilterClear = () => {
    setPendingStatus([]);
    setPendingType([]);
    handleClearFilter();
  };

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex items-center">
          <Search size={20} className="z-2 absolute left-4 top-3" />
          <Input
            className="ml-1 w-48 rounded-3xl pl-10 sm:w-64 md:w-80 lg:w-96"
            placeholder="Pesquisar"
            value={search}
            onChange={handleSearchInputChange}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={isFetching}
          className="rounded-lg"
          aria-label="Atualizar autorizações"
        >
          <RefreshCw
            size={18}
            className={isFetching ? "animate-spin" : undefined}
          />
        </Button>

        <BasicFilter>
          <form onSubmit={handleFilterSubmit}>
            <div className="space-y-4 p-4">
              <div className="space-y-2">
                <h3 className="font-medium text-black04">Status</h3>
                <Select
                  options={authorizationStatusOptions}
                  isMulti
                  onChange={setPendingStatus}
                  value={pendingStatus}
                  placeholder="Selecione o(s) status"
                  styles={customStyles}
                  components={{ IndicatorSeparator: () => null, DropdownIndicator }}
                />
              </div>
              <div className="space-y-2">
                <h3 className="font-medium text-black04">Produto</h3>
                <Select
                  options={authorizationTypeOptions}
                  isMulti
                  onChange={setPendingType}
                  value={pendingType}
                  placeholder="Selecione o(s) produto(s)"
                  styles={customStyles}
                  components={{ IndicatorSeparator: () => null, DropdownIndicator }}
                />
              </div>
            </div>
            <div className="my-5 w-full border-b-[1px] border-gray01" />
            <div className="flex items-center justify-between p-4">
              <Button
                type="button"
                onClick={handleFilterClear}
                variant="outline"
                className="w-24 rounded-full bg-gray02 text-xs font-bold"
              >
                Limpar
              </Button>
              <Button
                type="submit"
                className="w-20 rounded-full bg-primary text-xs font-bold text-white01"
              >
                Filtrar
              </Button>
            </div>
          </form>
        </BasicFilter>
      </div>

      <div className="block md:hidden">
        <MobileAuthorizationList
          items={data}
          onClick={handleRowClick}
          onLoadMore={fetchNextPage}
          isLoadingMore={isFetchingNextPage}
          hasMore={shouldShowInfiniteScroll}
          products={catalog}
        />
      </div>

      <div className="hidden md:block">
        <BasicTable<PreTransaction>
          table={table}
          type="only header"
          isLoading={isLoading || isFetching}
          handleRowClick={handleRowClick}
          isFetchingNextPage={isFetchingNextPage}
          observerRef={observerRef}
          shouldShowInfiniteScroll={shouldShowInfiniteScroll}
        />
      </div>

      <div ref={observerRef} />

      <AuthorizationSheet
        open={sheetOpen}
        setOpen={setSheetOpen}
        selectedItem={selectedItem}
        onSimulate={handleSimulate}
      />

      <ModalSimulatePan
        open={simulatePanOpen}
        onOpenChange={setSimulatePanOpen}
        preTransactionId={selectedItem?._id ?? ''}
        customerId={selectedItem?.customerId?._id ?? ''}
      />
    </>
  );
};
